import { Request } from "../stores/Requests20";
import { handleServiceResponse } from "./ServerRequestsInternal";
import { mapToQuery } from "../utils/UrlUtil";
import type { BasicStoreConfig, StoreConfig } from "../model/StoreInformation";
const BASE_URL = "/ordercapture/storeinfo";
const TIMEOUT = 5000;
// Delivery dates
type GetDeliveryDatesParams = {
	country: string;
	storeId: string;
	customerId: string;
	fsdAddressId: string;
};
type DeliveryDatesResponse = {
	deliveryDates: Record<string, ValidDeliveryDate | InvalidDeliveryDate>;
};
type DeliveryDate = {
	type: "ValidDate" | "InvalidDate";
	isCustomerSlot: boolean;
};
export type ValidDeliveryDate = DeliveryDate & {
	type: "ValidDate";
	deliveryTimes: DeliveryTimeSlot[];
	isAlternative: boolean;
	validUntil: string;
	leadTimeCompliantUntil: string;
};
export type InvalidDeliveryDate = DeliveryDate & {
	type: "InvalidDate";
	isStoreHoliday: boolean;
	alternativeDates: string[];
};
export type DeliveryTimeSlot = {
	fromUtc: string;
	untilUtc: string;
};
export async function getDeliveryDates({
	country,
	storeId,
	customerId,
	fsdAddressId,
}: GetDeliveryDatesParams): Promise<DeliveryDatesResponse> {
	const queryParams = new Map([
		["country", country],
		["storeId", storeId],
		["customerId", customerId],
		["fsdAddressId", fsdAddressId],
	]);
	const url = `${BASE_URL}/delivery-dates?${mapToQuery(queryParams)}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data;
}
// Pickup dates
type GetPickupDatesParams = {
	country: string;
	storeId: string;
};
type PickupDatesResponse = {
	pickupDates: Record<string, ValidPickupDate | InvalidPickupDate>;
};
type PickupDate = {
	type: "ValidDate" | "InvalidDate";
};
export type PickupTimeSlot = {
	fromDateTimeUTC: string;
	untilDateTimeUTC: string;
	availableCapacity: number;
};
export type ValidPickupDate = PickupDate & {
	type: "ValidDate";
	validUntil: string;
	leadTimeCompliantUntil: string;
	pickupTimeSlots: PickupTimeSlot[];
	isCustomerSlot: boolean;
};
export type InvalidPickupDate = PickupDate & {
	type: "InvalidDate";
	isStoreHoliday: boolean;
};

export const isPickupDate = (date: ValidPickupDate | ValidDeliveryDate): date is ValidPickupDate => {
	return (date as ValidPickupDate).pickupTimeSlots !== undefined;
};

export async function getPickupDates({ country, storeId }: GetPickupDatesParams): Promise<PickupDatesResponse> {
	const queryParams = new Map([
		["country", country],
		["storeId", storeId],
	]);
	const url = `${BASE_URL}/pickup-dates?${mapToQuery(queryParams)}`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data;
}
export async function getMapUrl(params: { country: string; storeId: string }): Promise<string> {
	const url = `${BASE_URL}/stores/${params.country}/${params.storeId}/map`;
	const response = Request.get(url).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data;
}
export async function getCountryStores(params: { country: string }): Promise<Record<string, BasicStoreConfig>> {
	const countryCode = params.country;
	const requestUrl = `${BASE_URL}/stores/${countryCode}`;
	const response = Request.get(requestUrl).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data.stores;
}
export async function getStoreConfig(params: { country: string; storeId: string }): Promise<StoreConfig> {
	const countryCode = params.country;
	const storeId = params.storeId;
	const requestUrl = `${BASE_URL}/stores/${countryCode}/${storeId}`;
	const response = Request.get(requestUrl).timeout(TIMEOUT).execute();
	return (await handleServiceResponse(response)).data;
}
